import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Resource } from '../../types/resources';
import { deleteResource } from '../../features/resources/resourceSlice';
import EditResourceForm from './EditResourceForm';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { formatDate } from '../../utils/formatters';
import { AlertTriangle } from 'react-feather';

interface Props {
  resource: Resource;
}

const ResourceCard: React.FC<Props> = ({ resource }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  const handleDelete = async () => {
    if (window.confirm(t('common.areYouSure'))) {
      await dispatch(deleteResource(resource.id));
    }
  };

  const isLowStock = resource.minimum_quantity && resource.quantity <= resource.minimum_quantity;
  const isExpiringSoon = resource.expiry_date && 
    new Date(resource.expiry_date).getTime() - new Date().getTime() < 30 * 24 * 60 * 60 * 1000;

  if (isEditing) {
    return (
      <EditResourceForm
        resource={resource}
        onCancel={() => setIsEditing(false)}
        onSuccess={() => setIsEditing(false)}
      />
    );
  }

  return (
    <Card>
      <div className="space-y-4">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{resource.name}</h3>
            <p className="text-sm text-gray-600">{t(`resources.category.${resource.category}`)}</p>
          </div>
          {(isLowStock || isExpiringSoon) && (
            <AlertTriangle 
              className="text-yellow-500" 
              title={isLowStock ? t('resources.lowStock') : t('resources.expiringSoon')}
            />
          )}
        </div>

        <div className="space-y-2 text-sm text-gray-600">
          <p>
            {t('Quantity')}: {resource.quantity} {resource.unit}
          </p>
          {resource.minimum_quantity && (
            <p>
              {t('Minimum Quantity')}: {resource.minimum_quantity} {resource.unit}
            </p>
          )}
          {resource.location && (
            <p>{t('Resource Location')}: {resource.location}</p>
          )}
          {resource.expiry_date && (
            <p>{t('Expiry Date')}: {formatDate(resource.expiry_date)}</p>
          )}
          {resource.notes && <p className="italic">{resource.notes}</p>}
        </div>

        <div className="flex justify-end space-x-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setIsEditing(true)}
          >
            {t('common.edit')}
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={handleDelete}
          >
            {t('common.delete')}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default ResourceCard;